import axiosConn from '../axiosConn';

export const TASK_PROMO_TIMEOUT_MS = 25000;
export const TASK_PROMO_STORAGE_PREFIX = 'jg:localops:task-promo:v1:';
const keyFor = (accountId) => {
  if (!accountId) throw new Error('Sign in before using a promo code.');
  return `${TASK_PROMO_STORAGE_PREFIX}${accountId}`;
};
const storage = () => window.localStorage;
const clone = (value) => JSON.parse(JSON.stringify(value));
const resolved = (state) => ['committed', 'closed_uncommitted'].includes(state);
const requestKeyFor = (accountId, requestKey) => `${keyFor(accountId)}:request:${requestKey}`;
const resultKeyFor = (accountId, requestKey) => `${requestKeyFor(accountId, requestKey)}:result`;
const staleResponse = () => new Error('This promo response is no longer current. Check the saved request.');
const parse = (raw) => {
  try { return JSON.parse(raw); } catch (_) { throw new Error('The saved promo request could not be read. Preserve browser storage and contact support.'); }
};
const savedEntries = (accountId, store) => {
  if (!accountId) return [];
  const legacyKey = keyFor(accountId);
  const prefix = `${legacyKey}:request:`;
  // Each sent DTO has its own immutable entry. Even simultaneous tab submissions
  // remain recoverable, one at a time; responses never write these entries.
  const keys = new Set([legacyKey]);
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key?.startsWith(prefix) && !key.endsWith(':result')) keys.add(key);
  }
  const entries = [];
  for (const key of keys) {
    const raw = store.getItem(key);
    if (!raw) continue;
    const record = parse(raw);
    if (!record || record.accountId !== String(accountId) || !record.requestKey || !record.task || record.promoCode !== 'CAMDEN15' ||
        (key !== legacyKey && key !== requestKeyFor(accountId, record.requestKey))) {
      throw new Error('The saved promo request needs recovery. Preserve browser storage and contact support.');
    }
    const savedResult = store.getItem(resultKeyFor(accountId, record.requestKey));
    const result = savedResult ? parse(savedResult) : record.result;
    if (savedResult && (!result?.success || !resolved(result.submissionOutcome))) {
      throw new Error('The saved promo result needs recovery. Preserve browser storage and contact support.');
    }
    entries.push({ key, record: result && resolved(result.submissionOutcome)
      ? { ...record, state: result.submissionOutcome, result } : record });
  }
  return entries.sort((a, b) => String(a.record.submittedAt).localeCompare(String(b.record.submittedAt)) ||
    a.record.requestKey.localeCompare(b.record.requestKey));
};
export const readTaskPromoSubmission = (accountId, store = storage()) => savedEntries(accountId, store)[0]?.record || null;
export const isCurrentTaskPromoSubmission = (accountId, requestKey, store = storage()) =>
  Boolean(requestKey && readTaskPromoSubmission(accountId, store)?.requestKey === requestKey);
const persist = (key, value, store) => {
  const encoded = JSON.stringify(value);
  try {
    store.setItem(key, encoded);
    if (store.getItem(key) !== encoded) throw new Error('Storage did not retain the request.');
  } catch (_) {
    throw new Error('Could not save the promo result in this browser. Preserve browser storage and check the original request before replacing it.');
  }
  return value;
};
export const beginTaskPromoSubmission = (accountId, promoCode, task, store = storage()) => {
  if (readTaskPromoSubmission(accountId, store)) throw new Error('Resolve your saved promo request before publishing another task.');
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const requestKey = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  try {
    return persist(requestKeyFor(accountId, requestKey), { accountId: String(accountId), promoCode: String(promoCode).trim().toUpperCase(),
      requestKey, task: clone(task), state: 'pending', submittedAt: new Date().toISOString() }, store);
  } catch (_) {
    throw new Error('Could not save the promo request in this browser. Nothing new was sent. Enable browser storage before trying again.');
  }
};
export const clearResolvedTaskPromoSubmission = (accountId, requestKey, store = storage()) => {
  if (!requestKey) throw staleResponse();
  const entry = savedEntries(accountId, store).find(({ record }) => record.requestKey === requestKey);
  if (!entry) return;
  if (!resolved(entry.record.state)) {
    throw new Error('Check or stop the pending request before starting another task.');
  }
  // Exact request key only. Never remove an account-wide slot now owned by B.
  // A late response can at worst leave an ignored result without a request entry.
  store.removeItem(entry.key);
  if (store.getItem(entry.key)) throw new Error('Could not clear the resolved request. Please try again.');
  store.removeItem(resultKeyFor(accountId, requestKey));
};
export const taskPromoErrorMessage = (err) => err?.response?.status === 404
  ? 'Promo service is unavailable. No ordinary funding was attempted. Check or stop any saved request before replacing it.'
  : err?.response?.data?.message || err?.message || 'Could not confirm the promo request. Check its result or retry the original.';

export const runTaskPromoAction = async ({ accountId, promoCode = 'CAMDEN15', task, action, requestKey,
  headers, client = axiosConn, store = storage() }) => {
  const config = { headers, timeout: TASK_PROMO_TIMEOUT_MS };
  if (action === 'preview') {
    if (readTaskPromoSubmission(accountId, store)) throw new Error('Resolve your saved promo request first.');
    const response = await client.post('local-ops/tasks/promo-preview', { promoCode, task }, config);
    return response.data;
  }
  const record = action === 'create' ? beginTaskPromoSubmission(accountId, promoCode, task, store)
    : readTaskPromoSubmission(accountId, store);
  if (!record) throw new Error('No saved promo request was found for this account.');
  if (requestKey && requestKey !== record.requestKey) throw staleResponse();
  const creating = action === 'create' || action === 'retry';
  if (!creating && !['status', 'close'].includes(action)) throw new Error('Unknown promo recovery action.');
  const body = { promoCode: record.promoCode, requestKey: record.requestKey,
    ...(creating || action === 'close' ? { task: record.task } : {}),
    ...(!creating ? { action } : {}) };
  try {
    const response = await client.post(creating ? 'local-ops/tasks/promo' : 'local-ops/tasks/promo-recovery', body, config);
    const result = response.data;
    if (!result?.success || !['committed', 'closed_uncommitted', 'unknown'].includes(result.submissionOutcome)) {
      throw new Error('The server did not confirm the submission outcome. Check the saved request.');
    }
    if (result.submissionOutcome === 'closed_uncommitted' && result.safeToReplace !== true) {
      throw new Error('The server did not confirm that this request is stopped. Check again before replacing it.');
    }
    const current = readTaskPromoSubmission(accountId, store);
    if (!current || current.requestKey !== record.requestKey) throw staleResponse();
    if (resolved(current.state) && result.submissionOutcome !== current.state) throw staleResponse();
    // Unknown is an observation, not permission to overwrite durable resolution.
    // Confirmed submission outcomes live separately from immutable request data.
    // Later committed task/financial-state responses may still update that result.
    if (resolved(result.submissionOutcome)) persist(resultKeyFor(accountId, record.requestKey), clone(result), store);
    if (!isCurrentTaskPromoSubmission(accountId, record.requestKey, store)) throw staleResponse();
    return { ...result, requestKey: record.requestKey };
  } catch (err) {
    // Never clear an uncertain key, even after validation errors or an old-server 404.
    // The original persisted DTO is sufficient for manual Check / Retry / Close.
    throw new Error(taskPromoErrorMessage(err));
  }
};
